<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Message from 'primevue/message';
import type { MenuItem } from 'primevue/menuitem';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { useKycData, type RiskProfileOption } from '@/composables/useKycData';
import { getRiskLevelDisplay, normalizeRiskLevelSlug, RISK_LEVEL_CONFIG } from '@/utils/kyc';
import { formatDateTime } from '@/utils/formatters';
import { formatCurrency } from '@/utils/currency';
import KycPersonChecksAccordion from '@/components/person-kyc/KycPersonChecksAccordion.vue';
import KycDeletedProfileNotice from '@/components/company-kyc/KycDeletedProfileNotice.vue';
import KycProfileReportControlsCard from '@/components/company-kyc/KycProfileReportControlsCard.vue';
import KycRiskSummaryCard from '@/components/company-kyc/KycRiskSummaryCard.vue';
import KycDecisionStatusCard from '@/components/company-kyc/KycDecisionStatusCard.vue';
import KycNoReportForPolicyCard from '@/components/company-kyc/KycNoReportForPolicyCard.vue';
import KycNoReportsSelectionCard from '@/components/company-kyc/KycNoReportsSelectionCard.vue';
import TokenActionConfirmationDialog from '@/components/common/TokenActionConfirmationDialog.vue';
import PolicyDecisionOptIn from '@/components/common/PolicyDecisionOptIn.vue';
import { useParecerQueries, type ParecerState } from '@/composables/useParecerQueries';
import { getCurrentParecerDecision, type EntityParecerStatus } from '@/composables/parecerStatus';
import workflowApi from '@/api/modules/workflow';
import parecerApi, { PARECER_START_STAGE_SLUG } from '@/api/modules/parecerApi';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useToast } from 'primevue/usetoast';
import { PARECER_BLOCKED_REASONS } from '@/constants/parecerMessages';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const canEditParecer = ref(false);
const parecerWorkflowMissing = ref(false);
const parecerRequiredRoles = ref<string[]>([]);

const entityType = 'person' as const;
const entityId: ComputedRef<string> = computed(() => route.params.uuid as string);

const {
  kycChecks,
  loading: kycSummaryLoading,
  riskProfilesLoading,
  error,
  entities,
  entitiesLoading,
  riskProfiles,
  selectedRiskProfile: selectedProfile,
  selectedRiskProfileDeleted,
  selectedRiskProfileConfigRoute,
  selectedRiskProfileId,
  selectedReportRequestId,
  categoryOrder,
  initialize,
  currentProfileHasReport,
  anyProfileHasReports,
  requestNewReport,
  relationshipTypeOptions,
  relationshipTypeOptionsSource,
  isGeneratingReport,
  generationProgressLabel,
  overallRiskLevelValue
} = useKycData({ entityId, entityType });

const selectedPricingSummary = computed(() => selectedProfile.value?.pricing_summary ?? null);

const reportCostBreakdown = computed(() => {
  const pricing = selectedPricingSummary.value;
  if (!pricing) return [];

  return [
    {
      label: 'Custo mínimo',
      value: formatCurrency(pricing.base_price, 'CIAL_TOKEN', true),
      helper: pricing.free_entities > 0
        ? `Inclui até ${pricing.free_entities} entidade(s) processadas neste relatório.`
        : 'Aplicado ao iniciar a geração deste relatório.',
      emphasis: true,
    },
    {
      label: 'Custo variável',
      value: `+${formatCurrency(pricing.unit_variable_cost, 'CIAL_TOKEN', true)} por entidade extra`,
      helper: pricing.free_entities > 0
        ? `Aplicado somente se o relatório resolver mais de ${pricing.free_entities} entidade(s), a partir da entidade nº ${pricing.free_entities + 1}.`
        : 'Aplicado sobre cada entidade processada.',
    },
  ];
});

const reportCostError = computed(() =>
  selectedPricingSummary.value ? null : 'Não foi possível carregar o preço desta política.'
);

const showNewReportConfirmDialog = ref(false);

// Whether the selected policy has automatic decisioning configured — only then do we
// offer the auto-parecer opt-in inside the confirmation dialog.
const selectedProfileHasPolicyDecision = computed(() => selectedProfile.value?.has_policy_decision === true);
const usePolicyDecisionChoice = ref(false);
const policyDecisionRelationshipIds = ref<string[]>([]);

// Pre-select all of the selected policy's relationship types by default (the parecer covers
// the whole policy unless the user narrows it). Options are policy-scoped and refetched on
// profile change, so mirroring them here also drops any ids from a previously selected policy.
// When the policy defines none the options are the account's, which the user picks explicitly.
watch(relationshipTypeOptions, (options) => {
  policyDecisionRelationshipIds.value = relationshipTypeOptionsSource.value === 'policy'
    ? options.map((option) => option.value)
    : [];
}, { immediate: true });

// Block confirmation while no pricing, while generating, or when auto decision is on but
// no relationship type was picked (the parecer must be associated with at least one).
const newReportConfirmDisabled = computed(() =>
  !selectedPricingSummary.value
  || isGeneratingReport.value
  || (usePolicyDecisionChoice.value && policyDecisionRelationshipIds.value.length === 0),
);

const openNewReportConfirmDialog = () => {
  if (isGeneratingReport.value) return;
  // Auto decision is opt-in: default off.
  showNewReportConfirmDialog.value = true;
};

const confirmNewReportRequest = async () => {
  showNewReportConfirmDialog.value = false;
  await requestNewReport(
    selectedProfileHasPolicyDecision.value
      ? {
          usePolicyDecision: usePolicyDecisionChoice.value,
          relationshipTypeIds: usePolicyDecisionChoice.value ? policyDecisionRelationshipIds.value : [],
        }
      : undefined,
  );
};

const cancelNewReportRequest = () => {
  showNewReportConfirmDialog.value = false;
};

const openDecisionWizard = (requestId?: string) => {
  const targetRequestId = requestId ?? selectedReportRequestId.value;
  if (!targetRequestId) return;
  router.push({
    name: 'person-kyc-review-wizard',
    params: {
      uuid: route.params.uuid,
      requestId: targetRequestId,
    },
  });
};

const parecerMenuItems = computed<MenuItem[]>(() => [
  { label: 'Assistente (wizard)', icon: 'material-icons list', command: () => openDecisionWizard() },
]);

const { getEntityParecerStatus, getReportParecerStateMap, getReportParecerStateMapForRequestIds } = useParecerQueries();
const parecerStatus = ref<EntityParecerStatus>({
  currentParecer: null,
  latestFinalizedParecer: null,
  hasAnyParecer: false,
});

const loadParecerStatus = async () => {
  if (!entityId.value) return;
  parecerStatus.value = await getEntityParecerStatus(
    entityType, entityId.value, selectedReportRequestId.value,
  );
};

// Take a parecer out of the unassigned queue. The workflow engine both enforces who may do
// this (the pending -> under_analysis transition's required_roles) and auto-assigns the acting
// user, so this one call is the whole "take" — no separate assign step.
const handleTakeParecer = async (parecerId: string) => {
  const requestId = parecerStatus.value.currentParecer?.requestId;
  try {
    await parecerApi.transitionParecer(parecerId, { to_stage_slug: PARECER_START_STAGE_SLUG });
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Não foi possível assumir o parecer',
      detail: getApiErrorMessage(err, 'Tente novamente.'),
      life: 5000,
    });
    // The block may be a stale permission read, so re-sync before the user retries.
    await loadParecerStatus();
    return;
  }

  await loadParecerStatus();
  openDecisionWizard(requestId);
};

const systemRisk = computed(() => {
  if (overallRiskLevelValue.value !== null) {
    return getRiskLevelDisplay(overallRiskLevelValue.value);
  }
  return null;
});

const riskSource = computed<'system' | 'finalized_parecer_difference'>(() => {
  const currentParecer = parecerStatus.value.currentParecer;
  const latestDecision = getCurrentParecerDecision(currentParecer);

  if (
    currentParecer?.currentStage.slug !== 'finalized'
    || !systemRisk.value
    || !currentParecer.finalDecision?.riskLevel
    || latestDecision?.lane !== 'final'
  ) {
    return 'system';
  }

  const finalRiskSlug = normalizeRiskLevelSlug(currentParecer.finalDecision.riskLevel);
  const systemRiskSlug = getRiskLevelDisplay(overallRiskLevelValue.value).slug;

  return finalRiskSlug !== systemRiskSlug ? 'finalized_parecer_difference' : 'system';
});

const overallRisk = computed(() => {
  if (!systemRisk.value) return null;
  if (riskSource.value !== 'finalized_parecer_difference') return systemRisk.value;

  const finalRiskSlug = normalizeRiskLevelSlug(parecerStatus.value.currentParecer?.finalDecision?.riskLevel);
  const config = RISK_LEVEL_CONFIG[finalRiskSlug ?? 'null'];

  return {
    slug: finalRiskSlug,
    ...config,
    levelValue: systemRisk.value.levelValue,
  };
});

type ReportSummary = RiskProfileOption['reports'][number];

const toReportTimestamp = (dateValue?: string | null) => {
  if (!dateValue) return 0;
  const parsed = new Date(dateValue).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatReportDateTime = (dateValue?: string | null) => formatDateTime(dateValue, '—');

const selectedProfileReports = computed<ReportSummary[]>(() => {
  const reports = selectedProfile.value?.reports ?? [];
  return [...reports].sort((a, b) => toReportTimestamp(b.report_generated_at) - toReportTimestamp(a.report_generated_at));
});

const selectedReportMeta = computed(() =>
  selectedProfileReports.value.find(report => report.request_id === selectedReportRequestId.value) ?? null
);

const activeInstanceId = computed(() =>
  selectedProfile.value?.current_kyx_risk_model_instance_id
  || selectedProfileReports.value[0]?.kyx_risk_model_instance_id
  || null
);

const activeInstanceVersion = computed(() =>
  selectedProfile.value?.current_kyx_risk_model_instance_version_number ?? null
);

const reportInstanceStory = computed(() => {
  const reportVersion = selectedReportMeta.value?.risk_model_instance_version_number;
  const currentVersion = activeInstanceVersion.value;

  if (reportVersion !== null && reportVersion !== undefined && currentVersion !== null && currentVersion !== undefined) {
    if (reportVersion === currentVersion) return `Usou v${reportVersion} (atual)`;
    return `Usou v${reportVersion} · atual v${currentVersion}`;
  }
  if (reportVersion !== null && reportVersion !== undefined) {
    return `Usou v${reportVersion}`;
  }
  if (currentVersion !== null && currentVersion !== undefined) {
    return `Atual v${currentVersion}`;
  }
  return null;
});

const isReportFromActiveInstance = (report: ReportSummary) => {
  if (activeInstanceVersion.value !== null && activeInstanceVersion.value !== undefined) {
    if (report.risk_model_instance_version_number !== null && report.risk_model_instance_version_number !== undefined) {
      return report.risk_model_instance_version_number === activeInstanceVersion.value;
    }
  }
  if (!report.kyx_risk_model_instance_id || !activeInstanceId.value) return false;
  return report.kyx_risk_model_instance_id === activeInstanceId.value;
};

const getReportVersionStatus = (report: ReportSummary) => {
  if (isReportFromActiveInstance(report)) {
    return { label: 'Versão atual', class: 'bg-emerald-100 text-emerald-700 border border-emerald-200' };
  }
  if (activeInstanceVersion.value !== null || activeInstanceId.value) {
    return { label: 'Versão anterior', class: 'bg-rose-100 text-rose-700 border border-rose-200' };
  }
  return { label: 'Versão não identificada', class: 'bg-slate-100 text-slate-600 border border-slate-200' };
};

const mapParecerStateToBadge = (state: ParecerState, stageSlug?: string) => {
  if (stageSlug === 'manager_review') {
    return { label: 'Aguardando gerente', class: 'bg-amber-100 text-amber-700 border border-amber-200' };
  }
  // Queued: the parecer exists but nobody owns it, so it must not read as "em andamento".
  if (stageSlug === 'pending') {
    return { label: 'Parecer pendente', class: 'bg-amber-100 text-amber-700 border border-amber-200' };
  }
  switch (state) {
    case 'approved':
      return { label: 'Parecer aprovado', class: 'bg-emerald-100 text-emerald-700 border border-emerald-200' };
    case 'rejected':
      return { label: 'Parecer reprovado', class: 'bg-rose-100 text-rose-700 border border-rose-200' };
    case 'expired':
      return { label: 'Parecer expirado', class: 'bg-orange-100 text-orange-700 border border-orange-200' };
    case 'in_progress':
      return { label: 'Parecer em andamento', class: 'bg-blue-100 text-blue-700 border border-blue-200' };
    default:
      return { label: 'Sem parecer', class: 'bg-slate-100 text-slate-600 border border-slate-200' };
  }
};

const parecersByRequestId = ref(new Map<string, { state: ParecerState; stageSlug?: string }>());

const reportParecerBadgeMap = computed(() => {
  const badges = new Map<string, { label: string; class: string }>();
  for (const report of selectedProfileReports.value) {
    const info = parecersByRequestId.value.get(report.request_id);
    if (info) {
      badges.set(report.request_id, mapParecerStateToBadge(info.state, info.stageSlug));
    } else {
      badges.set(report.request_id, mapParecerStateToBadge('none'));
    }
  }
  return badges;
});

const getReportParecerBadge = (report: ReportSummary) =>
  reportParecerBadgeMap.value.get(report.request_id) ?? mapParecerStateToBadge('none');

const isSelectedReportOnOldProfileVersion = computed(() => {
  if (!selectedReportMeta.value) return false;
  return !isReportFromActiveInstance(selectedReportMeta.value);
});

// currentParecer prefers the selected report's parecer, so the selected report has a
// parecer exactly when currentParecer points back at it. Deriving it from parecerStatus
// (rather than the separately-loaded badge map) keeps this in sync with the finalized
// block the card renders, avoiding a flash of "Nova análise" before the badges load.
const selectedReportHasParecer = computed(() =>
  !!selectedReportRequestId.value
  && parecerStatus.value.currentParecer?.requestId === selectedReportRequestId.value
);

// Highest-priority reason first. An existing parecer leads because it is the most actionable
// ("go look at it") rather than an error. The permission reason is not here: the card owns it
// and slots it between this list's first entry and the rest (see KycDecisionStatusCard).
const startParecerBlockedReason = computed<string | null>(() => {
  if (selectedReportHasParecer.value) return PARECER_BLOCKED_REASONS.reportHasParecer;
  if (selectedRiskProfileDeleted.value) return PARECER_BLOCKED_REASONS.deletedPolicy;
  return null;
});

const canStartParecer = computed(() => startParecerBlockedReason.value === null);

const profileReportControlsProps = computed(() => ({
  selectedRiskProfileId: selectedRiskProfileId.value,
  selectedReportRequestId: selectedReportRequestId.value,
  riskProfiles: riskProfiles.value,
  selectedProfileReports: selectedProfileReports.value,
  selectedReportMeta: selectedReportMeta.value,
  isGeneratingReport: isGeneratingReport.value,
  profileConfigRoute: selectedRiskProfileConfigRoute.value,
  reportInstanceStory: reportInstanceStory.value,
  isSelectedReportOnOldProfileVersion: isSelectedReportOnOldProfileVersion.value,
  formatReportDateTime,
  getReportVersionStatus,
  getReportParecerBadge,
}));

const handleSelectedRiskProfileChange = (value: string | null) => {
  selectedRiskProfileId.value = value;
};

const handleSelectedReportRequestChange = (value: string | null) => {
  selectedReportRequestId.value = value;
};

const contentViewState = computed<'has-report' | 'generating' | 'no-report-for-profile' | 'no-reports'>(
  () => {
    if (currentProfileHasReport.value) return 'has-report';
    if (isGeneratingReport.value) return 'generating';
    if (anyProfileHasReports.value) return 'no-report-for-profile';
    return 'no-reports';
  }
);

const passedChecksCount = computed(() => kycChecks.value.filter(c => c.status === 'passed').length);
const failedChecksCount = computed(() => kycChecks.value.filter(c => c.status === 'failed').length);

const loadAllParecerBadges = async () => {
  if (!entityId.value) return;
  try {
    const reportRequestIds = riskProfiles.value.flatMap((profile) =>
      profile.reports.map((report) => report.request_id)
    );
    parecersByRequestId.value = reportRequestIds.length > 0
      ? await getReportParecerStateMapForRequestIds(reportRequestIds)
      : await getReportParecerStateMap(entityType, entityId.value);
  } catch {
    // Fallback: leave map empty, badges default to 'Sem parecer'
  }
};

watch([entityId, selectedReportRequestId], async () => {
  await loadParecerStatus();
  await loadAllParecerBadges();
}, { immediate: true });

const handleOpenCurrentParecer = () => {
  const currentParecer = parecerStatus.value.currentParecer;
  if (!currentParecer) return;

  if (currentParecer.currentStage.slug === 'manager_review') {
    router.push({
      name: 'person-kyc-manager-review',
      params: { uuid: route.params.uuid, parecerId: currentParecer.parecerId },
    });
    return;
  }

  if (
    currentParecer.currentStage.slug === 'request_for_information'
    || currentParecer.currentStage.slug === 'finalized'
  ) {
    router.push({
      name: 'person-kyc-parecer-view',
      params: { uuid: route.params.uuid, requestId: currentParecer.requestId },
    });
  }
};

const handleOpenLatestFinalizedParecer = () => {
  const latestFinalizedParecer = parecerStatus.value.latestFinalizedParecer;
  if (!latestFinalizedParecer) return;

  router.push({
    name: 'person-kyc-parecer-view',
    params: { uuid: route.params.uuid, requestId: latestFinalizedParecer.requestId },
  });
};

const personEntity = computed(() => entities.value[0] || null);

const loadParecerWorkflowAccess = async () => {
  try {
    const access = await workflowApi.checkAccess('kyc_parecer');
    parecerWorkflowMissing.value = !access.hasActiveWorkflow;
    canEditParecer.value = access.canEditInitialStage;
    parecerRequiredRoles.value = access.requiredRoleNames ?? [];
  } catch (e) {
    console.warn('Failed to check parecer workflow access:', e);
    parecerWorkflowMissing.value = true;
    canEditParecer.value = false;
  }
};

onMounted(() => {
  initialize();
  loadParecerWorkflowAccess();
});
</script>

<template>
  <div class="p-2 sm:p-2 md:p-3">
    <div class="flex items-center gap-3 mb-5">
      <BaseIcon class="text-4xl text-gray-500">document_scanner</BaseIcon>
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Verificação KYX</h1>
      </div>
    </div>
    <div v-if="riskProfilesLoading" class="flex justify-center items-center h-96">
      <ProgressSpinner />
    </div>

    <div v-else>
      <div v-if="isGeneratingReport && currentProfileHasReport" class="mb-3">
        <div class="rounded-xl border border-sky-200 bg-sky-50/80 px-3 py-2.5 shadow-sm">
          <div class="flex items-start gap-2.5">
            <BaseIcon class="text-sky-600 text-lg animate-spin">autorenew</BaseIcon>
            <div class="min-w-0">
              <div class="text-sm font-semibold text-sky-900">Gerando novo relatório</div>
              <div class="text-xs text-sky-700 mt-0.5">{{ generationProgressLabel }}</div>
              <div class="text-[11px] text-sky-700/90 mt-0.5">Último relatório permanece visível até concluir.</div>
            </div>
          </div>
        </div>
      </div>

      <Transition name="kyc-panel-fade" mode="out-in">
        <div :key="`top-${contentViewState}`">
          <div v-if="currentProfileHasReport" class="mb-3">
            <div class="grid items-start gap-2.5 xl:items-stretch xl:grid-cols-[minmax(240px,0.88fr)_minmax(0,1.2fr)_minmax(360px,1fr)]">
              <KycRiskSummaryCard
                :overall-risk="overallRisk"
                :system-risk="systemRisk"
                :risk-source="riskSource"
                :passed-checks-count="passedChecksCount"
                :failed-checks-count="failedChecksCount"
              />

              <KycDecisionStatusCard
                :status="parecerStatus"
                :is-generating-report="isGeneratingReport"
                :parecer-menu-items="parecerMenuItems"
                :can-edit="canEditParecer"
                :can-start-parecer="canStartParecer"
                :start-parecer-blocked-reason="startParecerBlockedReason"
                :workflow-missing="parecerWorkflowMissing"
                :required-role-names="parecerRequiredRoles"
                @open-current-parecer="handleOpenCurrentParecer"
                @open-latest-finalized-parecer="handleOpenLatestFinalizedParecer"
                @open-decision-wizard="openDecisionWizard"
                @take-parecer="handleTakeParecer"
              />

              <KycProfileReportControlsCard
                class="h-full"
                v-bind="profileReportControlsProps"
                @update:selected-risk-profile-id="handleSelectedRiskProfileChange"
                @update:selected-report-request-id="handleSelectedReportRequestChange"
                @request-new-report="openNewReportConfirmDialog"
              />
            </div>
          </div>
          <div v-else-if="contentViewState === 'generating' && anyProfileHasReports" class="mb-3 flex justify-end">
            <KycProfileReportControlsCard
              class="w-full max-w-[430px]"
              v-bind="profileReportControlsProps"
              :show-report-section="false"
              @update:selected-risk-profile-id="handleSelectedRiskProfileChange"
              @update:selected-report-request-id="handleSelectedReportRequestChange"
              @request-new-report="openNewReportConfirmDialog"
            />
          </div>
          <div v-else-if="contentViewState === 'no-report-for-profile'" class="mb-3">
            <div class="grid items-start gap-2.5 xl:grid-cols-[minmax(240px,0.88fr)_minmax(0,1.2fr)_minmax(360px,1fr)]">
              <KycNoReportForPolicyCard
                :loading="kycSummaryLoading"
                @request-new-report="openNewReportConfirmDialog"
              />
              <KycProfileReportControlsCard
                class="w-full"
                v-bind="profileReportControlsProps"
                :show-report-section="false"
                @update:selected-risk-profile-id="handleSelectedRiskProfileChange"
                @update:selected-report-request-id="handleSelectedReportRequestChange"
                @request-new-report="openNewReportConfirmDialog"
              />
            </div>
          </div>
        </div>
      </Transition>

      <KycDeletedProfileNotice v-if="selectedRiskProfileDeleted" class="mb-4" />

      <div v-if="error" class="my-4">
        <Message severity="error" :closable="false">{{ error }}</Message>
      </div>

      <Transition name="kyc-content-fade" mode="out-in">
        <div :key="`content-${contentViewState}`">
          <div v-if="contentViewState === 'has-report'">
            <KycPersonChecksAccordion
              v-if="personEntity"
              :entity="personEntity"
              :category-order="categoryOrder"
              :loading="entitiesLoading || kycSummaryLoading"
              class="mt-8"
            />
          </div>

          <div v-else-if="contentViewState === 'generating'" class="flex justify-center py-8">
            <Card class="w-full max-w-lg">
              <template #content>
                <div class="text-center">
                  <ProgressSpinner class="mb-4" />
                  <h2 class="text-2xl font-semibold text-gray-900 mb-2">Gerando Relatório KYX</h2>
                  <p class="text-gray-600 mb-3">Isso pode levar alguns minutos. Por favor, aguarde...</p>
                  <p class="text-blue-600 font-medium">{{ generationProgressLabel }}</p>
                </div>
              </template>
            </Card>
          </div>

          <div v-else-if="contentViewState === 'no-report-for-profile'"></div>

          <div v-else class="flex justify-center py-8">
            <KycNoReportsSelectionCard
              :risk-profiles="riskProfiles"
              :selected-risk-profile-id="selectedRiskProfileId"
              :loading="kycSummaryLoading"
              @update:selected-risk-profile-id="selectedRiskProfileId = $event"
              @request-new-report="openNewReportConfirmDialog"
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>

  <TokenActionConfirmationDialog
    v-model:visible="showNewReportConfirmDialog"
    title="Confirmar Solicitação"
    message-prefix="Você está prestes a solicitar a geração de um novo relatório KYX"
    :cost-breakdown="reportCostBreakdown"
    :cost-error="reportCostError"
    :confirm-disabled="newReportConfirmDisabled"
    @confirm="confirmNewReportRequest"
    @cancel="cancelNewReportRequest"
  >
    <template v-if="selectedProfileHasPolicyDecision" #extra>
      <PolicyDecisionOptIn
        v-model="usePolicyDecisionChoice"
        v-model:relationship-type-ids="policyDecisionRelationshipIds"
        :decision-logic="selectedProfile?.decision_logic"
        :decision-version="selectedProfile?.decision_version"
        :relationship-type-options="relationshipTypeOptions"
      />
    </template>
  </TokenActionConfirmationDialog>
</template>

<style scoped>
.kyc-panel-fade-enter-active,
.kyc-panel-fade-leave-active,
.kyc-content-fade-enter-active,
.kyc-content-fade-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.kyc-panel-fade-enter-from,
.kyc-panel-fade-leave-to,
.kyc-content-fade-enter-from,
.kyc-content-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
